import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/axios";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import Combobox from "@/components/Combobox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DataPendaftaran, Unitkerja } from "@/types";
import { getListUnitkerja } from "@/services/getData";
import Modal from "@/components/Modal";
import FormHeader from "@/components/FormHeader";

interface FormPindahProdiProps {
  selectedRow: DataPendaftaran | null;
  closeModal: () => void;
}

const postDataPindahProdi = async ({
  id_data_pendaftaran,
  uk_id_tujuan,
  catatan,
}: {
  id_data_pendaftaran: string;
  uk_id_tujuan: string;
  catatan: string;
}) => {
  const response = await api.post("/data-pendaftaran/pindah-prodi", {
    id_data_pendaftaran,
    uk_id_tujuan,
    catatan,
  });

  return response.data;
};

export default function FormPindahProdi({
  selectedRow,
  closeModal,
}: FormPindahProdiProps) {
  const [selectedUkId, setSelectedUkId] = useState<string>("");
  const [openCombobox, setOpenCombobox] = useState(false);
  const [catatan, setCatatan] = useState<string>("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const queryClient = useQueryClient();

  const { data: listUnitKerja, isPending: isPendingUnitKerja } = useQuery({
    queryKey: ["listUnitKerja"],
    queryFn: getListUnitkerja,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: postDataPindahProdi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] });
      setShowConfirmModal(false);
      closeModal();
      toast.success(data?.message || "Berhasil pindah prodi.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "Terjadi kesalahan yang tidak terduga.",
      );
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedUkId) {
      toast.error("Pilih Program Studi Tujuan terlebih dahulu.");
      return;
    }
    if (!selectedRow?.id_data_pendaftaran) {
      toast.error("Data Pendaftaran tidak ditemukan.");
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    mutate({
      id_data_pendaftaran: String(selectedRow?.id_data_pendaftaran),
      uk_id_tujuan: selectedUkId,
      catatan: catatan,
    });
  };

  const unitKerjaOptions =
    listUnitKerja
      ?.filter(
        (uk: Unitkerja) =>
          String(uk.unitkerja_group_key) === "5" && uk.is_active,
      )
      .map((uk: Unitkerja) => ({
        value: String(uk.uk_id),
        label: uk.nama || uk.name || "",
      })) || [];

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-2">
          <Label>Peserta</Label>
          <div className="p-2 bg-gray-100 rounded-md border text-sm">
            {selectedRow?.data_siswa?.nama_lengkap} (
            {selectedRow?.nomor_pendaftaran})
            <br />
            {selectedRow?.is_lulus ? (
              <span className="text-gray-500 text-xs">
                Prodi Saat Ini:{" "}
                {
                  selectedRow?.data_pilihan_prodi?.find(
                    (p: any) => p.is_lulus === 1,
                  )?.unitkerja?.nama
                }
              </span>
            ) : (
              <span className="text-destructive text-xs italic">
                Peserta belum lulus
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-y-2">
          <Label>Program Studi Tujuan</Label>
          <Combobox
            open={openCombobox}
            setOpen={setOpenCombobox}
            options={unitKerjaOptions}
            value={selectedUkId}
            setValue={setSelectedUkId}
            placeholder="Pilih Program Studi"
            isPending={isPendingUnitKerja}
          />
        </div>

        <div className="flex flex-col gap-y-2">
          <Label>Catatan</Label>
          <Textarea
            placeholder="Tuliskan catatan pindah prodi..."
            value={catatan}
            onChange={(e) => setCatatan(e.target.value)}
            rows={4}
          />
        </div>

        <div className="flex gap-x-3 mt-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={closeModal}
            disabled={isPending}
          >
            Batal
          </Button>
          <Button type="submit" disabled={isPending || !selectedUkId}>
            Simpan
          </Button>
        </div>
      </form>

      <Modal show={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <FormHeader
          title="Konfirmasi Pindah Prodi"
          description={`Apakah anda yakin ingin memindahkan program studi peserta ${selectedRow?.data_siswa?.nama_lengkap}?`}
          icon={<></>}
        />
        <div className="mt-5 flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowConfirmModal(false)}
            disabled={isPending}
          >
            Batal
          </Button>
          <Button onClick={handleConfirm} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Simpan"
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
